import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatTimeToDigit, formatDigitToTimeFrame } from '~/utils';
import { Chapters } from '@interfaces/pose.interface';

import { ReactComponent as IconRefresh } from '@svg/common/refresh_icon.svg';
import { MIN_DURATION_SEC, MIN_GAP_SEC } from '@utils/chapter';
import { TimeStampRegExp } from '@utils/format';
import { TTimeStampFormat } from '@interfaces/ui.interface';

interface TimeInputProps {
  focusEvent?: () => void;
  refreshEvent?: () => void;
  blurEvent?: (value?: string) => void;
  changeEvent?: (timeValue: string | null) => void;
  type: 'timeline' | 'new' | 'modify';
  duration: number;
  value: number;
  defaultValue: string;
  transcodingFrameRate: number;
  isEditDuration?: boolean;
  hasRefresh?: boolean;
  timeFormat: TTimeStampFormat;
  chapterData?: Chapters;
  currentRenameIdx?: number;
  isStart?: boolean;
}

const TimeInput: React.FC<TimeInputProps> = ({
  focusEvent,
  changeEvent,
  blurEvent,
  refreshEvent,
  type,
  duration,
  value,
  defaultValue,
  transcodingFrameRate,
  // isEditDuration,
  hasRefresh,
  timeFormat,
  chapterData,
  currentRenameIdx,
  isStart,
}) => {
  const { t } = useTranslation();
  const timeInput = useRef<HTMLInputElement | null>(null);
  const [inputTimeValue, setInputTimeValue] = useState<string | null>(defaultValue);
  const selectionRangeStart = useRef(-1);
  const selectionRangeEnd = useRef(-1);
  const [isSelectCaret, setSelectCaret] = useState(false);

  const timeUnitByCursor = (value: string, cursorPos: number) => {
    const absolutePos = cursorPos + (11 - value.length);
    let result;
    switch (true) {
      case absolutePos <= 2:
        result = 'hour';
        break;
      case absolutePos <= 5:
        result = 'min';
        break;
      case absolutePos <= 8:
        result = 'sec';
        break;
      default:
        result = 'frame';
        break;
    }
    return result;
  };

  const setSelectionRangeByCursor = (cursorPos: number) => {
    const start = Math.floor(cursorPos / 3) * 3;
    selectionRangeStart.current = start;
    selectionRangeEnd.current = start + 2;
  };

  const timeObjFromValue = (str: string) => {
    const result = {
      hour: -1,
      min: -1,
      sec: -1,
      frame: -1,
    };
    type TUnitKeys = keyof typeof result;
    const unitKeys: TUnitKeys[] = ['frame', 'sec', 'min', 'hour'];

    const unitValues = str.split(':').reverse(); // [frame, sec, min, hour]
    unitValues.forEach((val, idx) => {
      result[unitKeys[idx]] = +val;
    });

    return result;
  };

  const timeFrameString = useCallback(
    (timeDigit: number) => formatDigitToTimeFrame(timeDigit, transcodingFrameRate, timeFormat),
    [transcodingFrameRate, timeFormat],
  );

  const durationKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    selectionRangeStart.current = -1;
    selectionRangeEnd.current = -1;

    const charCode = e.which;

    // We only want to allow certain special characters and numbers
    // for our currency input (0-9, tab, delete, left and right arrow)
    if (
      !(
        (e.metaKey && charCode === 65) ||
        (e.shiftKey && charCode === 186) ||
        (!e.shiftKey &&
          (charCode === 27 ||
            charCode === 13 ||
            charCode === 8 ||
            charCode === 37 ||
            charCode === 38 ||
            charCode === 39 ||
            charCode === 40 ||
            charCode === 9 ||
            (charCode >= 48 && charCode <= 58) ||
            (charCode >= 96 && charCode <= 105) ||
            charCode === 190))
      )
    ) {
      e.preventDefault();
      return false;
    }

    const keyCode = e.keyCode;

    e.stopPropagation();

    if (keyCode === 13) {
      timeInput.current?.blur();
    } else if (keyCode === 27) {
      if (timeInput.current) {
        timeInput.current!.value = timeFrameString(value);
        timeInput.current?.blur();
      }
    } else if (keyCode >= 37 && keyCode <= 40) {
      if (keyCode === 38 || keyCode === 40) {
        e.preventDefault();
        setSelectCaret(true);
      } else {
        setSelectCaret(false);
      }
      inputArrowEvent(e);
    }
  };

  const inputArrowEvent = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keyCode = e.keyCode;
    const cursorStart = timeInput.current?.selectionStart;
    const cursorEnd = timeInput.current?.selectionEnd;
    let value = inputTimeValue;

    if (
      (!cursorStart && cursorStart !== 0) ||
      (!cursorEnd && cursorEnd !== 0) ||
      (!value && value !== '')
    ) {
      return;
    }

    const RegExp = TimeStampRegExp[timeFormat];

    if (value === '' || (typeof value === 'string' && !RegExp.test(value))) {
      value = timeFrameString(0);
    } else if (!isNaN(Number(value))) {
      value = timeFrameString(parseFloat(value));
    }

    switch (keyCode) {
      // left
      case 37:
        return;
      // up
      case 38: {
        const timeObj = timeObjFromValue(value);
        const timeType = timeUnitByCursor(value, cursorStart);
        setSelectionRangeByCursor(cursorStart);

        const newTimeObj = controlTime(timeObj, timeType, 'up');
        let formattedNewTime = Object.entries(newTimeObj)
          .reduce((acc, item) => {
            const val = item[1];
            if (val > -1) {
              acc += (val >= 10 ? val : '0' + val) + ':';
            }
            return acc;
          }, '')
          .slice(0, -1);

        if (type === 'modify' && chapterData && currentRenameIdx !== undefined) {
          const nextChapterStartTime =
            currentRenameIdx !== chapterData.length - 1
              ? chapterData[currentRenameIdx + 1].startTime - MIN_GAP_SEC
              : duration;
          const curEndBoundaryTime = chapterData[currentRenameIdx].endTime - MIN_DURATION_SEC;
          const newTime = formatTimeToDigit(formattedNewTime, transcodingFrameRate);

          if (isStart && curEndBoundaryTime <= newTime) {
            formattedNewTime = timeFrameString(curEndBoundaryTime);
          } else if (!isStart && newTime >= nextChapterStartTime) {
            formattedNewTime = timeFrameString(nextChapterStartTime);
          }
        } else {
          formattedNewTime =
            formatTimeToDigit(formattedNewTime, transcodingFrameRate) >= duration
              ? timeFrameString(duration)
              : formattedNewTime;
        }

        setInputTimeValue(formattedNewTime);

        // if (keyboardEvent) {
        //   keyboardEvent(formattedNewTime);
        // }
        if (changeEvent) changeEvent(formattedNewTime);
        return;
      }
      // right
      case 39:
        return;
      // down
      case 40: {
        const timeObj = timeObjFromValue(value);
        const timeType = timeUnitByCursor(value, cursorStart);
        setSelectionRangeByCursor(cursorStart);

        const newTimeObj = controlTime(timeObj, timeType, 'down');
        let formattedNewTime = Object.entries(newTimeObj)
          .reduce((acc, item) => {
            const val = item[1];
            if (val > -1) {
              acc += (val >= 10 ? val : '0' + val) + ':';
            }
            return acc;
          }, '')
          .slice(0, -1);

        const newTime = formatTimeToDigit(formattedNewTime, transcodingFrameRate);

        if (type === 'modify' && chapterData && currentRenameIdx !== undefined) {
          const prevEndTime =
            currentRenameIdx > 0 ? chapterData[currentRenameIdx - 1].endTime + MIN_GAP_SEC : 0;
          const curStartBoundaryTime = chapterData[currentRenameIdx].startTime + MIN_DURATION_SEC;

          if (isStart && prevEndTime >= newTime) {
            formattedNewTime = timeFrameString(prevEndTime);
          } else if (!isStart && newTime <= curStartBoundaryTime) {
            formattedNewTime = timeFrameString(curStartBoundaryTime);
          }
        } else {
          formattedNewTime =
            formatTimeToDigit(formattedNewTime, transcodingFrameRate) < 1
              ? timeFrameString(0)
              : formattedNewTime;
        }

        if (type === 'new' && newTime < MIN_DURATION_SEC) {
          formattedNewTime = timeFrameString(MIN_DURATION_SEC);
        }

        setInputTimeValue(formattedNewTime);

        // if (keyboardEvent) {
        //   keyboardEvent(formattedNewTime);
        // }
        if (changeEvent) changeEvent(formattedNewTime);
        return;
      }
      default:
        return;
    }
  };

  useEffect(() => {
    if (selectionRangeStart.current >= 0 && selectionRangeEnd.current >= 0)
      timeInput.current?.setSelectionRange(selectionRangeStart.current, selectionRangeEnd.current);

    setSelectCaret(false);
  }, [selectionRangeStart, selectionRangeEnd, timeInput, inputTimeValue]);

  useEffect(() => {
    setInputTimeValue(timeFrameString(value));
  }, [value, timeFrameString]);

  const controlTime = (
    time: { hour: number; min: number; sec: number; frame: number },
    timeType: string,
    type: string,
  ) => {
    switch (timeType + type) {
      case 'hourup':
        time['hour']++;
        break;
      case 'hourdown':
        if (time['hour'] > 0) {
          time['hour']--;
        }
        break;
      case 'minup':
        if (time['min'] < 59) {
          time['min']++;
        } else {
          time = controlTime(time, 'hour', 'up');
          time['min'] = 0;
        }
        break;
      case 'mindown':
        if (time['hour'] <= 0 && time['min'] < 1) break;
        if (time['min'] > 0) {
          time['min']--;
        } else {
          time = controlTime(time, 'hour', 'down');
          time['min'] = 59;
        }
        break;
      case 'secup':
        if (time['sec'] < 59) {
          time['sec']++;
        } else {
          time = controlTime(time, 'min', 'up');
          time['sec'] = 0;
        }
        break;
      case 'secdown':
        if (time['hour'] <= 0 && time['min'] <= 0 && time['sec'] < 1) break;
        if (time['sec'] > 0) {
          time['sec']--;
        } else {
          time = controlTime(time, 'min', 'down');
          time['sec'] = 59;
        }
        break;
      case 'frameup':
        if (time['frame'] < 29) {
          time['frame']++;
        } else {
          time = controlTime(time, 'sec', 'up');
          time['frame'] = 0;
        }
        break;
      case 'framedown':
        if (time['hour'] <= 0 && time['min'] <= 0 && time['sec'] <= 0 && time['frame'] < 1) break;
        if (time['frame'] > 0) {
          time['frame']--;
        } else {
          time = controlTime(time, 'sec', 'down');
          time['frame'] = 29;
        }
        break;
      default:
        break;
    }

    return time;
  };

  const timeInputBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value: curInputValue } = e.target;
    const RegExp = TimeStampRegExp[timeFormat];

    if (curInputValue === '' || (curInputValue.indexOf(':') >= 0 && !RegExp.test(curInputValue))) {
      setInputTimeValue(timeFrameString(value));
      return;
    }

    let newDuration = RegExp.test(curInputValue)
      ? formatTimeToDigit(curInputValue, transcodingFrameRate)
      : +curInputValue;

    if (type === 'new' && newDuration < MIN_DURATION_SEC) {
      newDuration = MIN_DURATION_SEC;
    }

    if (type === 'modify' && chapterData && currentRenameIdx !== undefined) {
      const prevChapterEndTime =
        currentRenameIdx !== 0 ? chapterData[currentRenameIdx - 1].endTime + MIN_GAP_SEC : 0;
      const nextChapterStartTime =
        currentRenameIdx !== chapterData.length - 1
          ? chapterData[currentRenameIdx + 1].startTime - MIN_GAP_SEC
          : duration;
      const currentChapter = chapterData[currentRenameIdx];

      if (isStart && prevChapterEndTime >= newDuration) {
        newDuration = prevChapterEndTime;
      } else if (isStart && newDuration >= currentChapter.endTime - MIN_DURATION_SEC) {
        newDuration = currentChapter.endTime - MIN_DURATION_SEC;
      }

      if (!isStart && newDuration >= nextChapterStartTime) {
        newDuration = nextChapterStartTime;
      } else if (!isStart && newDuration <= currentChapter.startTime + MIN_DURATION_SEC) {
        newDuration = currentChapter.startTime + MIN_DURATION_SEC;
      }
    }

    newDuration = newDuration >= duration ? duration : newDuration;

    setInputTimeValue(timeFrameString(newDuration));
    if (blurEvent) blurEvent(String(newDuration));
    else if (changeEvent) changeEvent(timeFrameString(newDuration));
  };

  const timeInputFocusHandler = () => {
    if (focusEvent) focusEvent();
  };

  const refreshHandler = () => {
    setInputTimeValue(defaultValue);
    if (refreshEvent) refreshEvent();
  };

  return (
    <TimeInputContainer
      type={type}
      isSelectCaret={isSelectCaret}
      isOverHour={timeFormat.indexOf('H') > -1}
    >
      {duration > 0 && inputTimeValue !== null && (
        <>
          <Tooltip
            placement="bottom"
            title={
              timeFormat === 'h:mm:ss:ff'
                ? t('video.editor.chapter.tooltip.hour') +
                  ' : ' +
                  t('video.editor.chapter.tooltip.minute') +
                  ' : ' +
                  t('video.editor.chapter.tooltip.second') +
                  ' : ' +
                  t('video.editor.chapter.tooltip.frame')
                : timeFormat === 'mm:ss:ff'
                ? t('video.editor.chapter.tooltip.minute') +
                  ' : ' +
                  t('video.editor.chapter.tooltip.second') +
                  ' : ' +
                  t('video.editor.chapter.tooltip.frame')
                : t('video.editor.chapter.tooltip.second') +
                  ' : ' +
                  t('video.editor.chapter.tooltip.frame')
            }
            trigger="focus"
          >
            <input
              ref={timeInput}
              type="text"
              className="time-picker"
              // defaultValue={duration >= 3600 ? '00:00:10:00' : duration >= 60 ? '00:10:00' : '10:00'}
              value={inputTimeValue}
              pattern="[0-9]*"
              maxLength={duration >= 3600 ? 11 : duration >= 60 ? 8 : 5}
              onChange={(e) => {
                const inputValue = e.target.value.replace(/[ㄱ-ㅎㅏ-ㅣ가-힣]/g, '');
                setInputTimeValue(inputValue);
              }}
              onKeyDown={(e) => durationKeyDown(e)}
              onFocus={() => timeInputFocusHandler()}
              onBlur={(e) => {
                timeInputBlurHandler(e);
              }}
            ></input>
          </Tooltip>
          {hasRefresh && refreshEvent && (
            <Tooltip
              placement="bottom"
              title={t('video.editor.chapter.tooltip.refresh')}
              trigger="hover"
            >
              <button className="btn-refresh" onClick={refreshHandler}>
                <IconRefresh />
              </button>
            </Tooltip>
          )}
        </>
      )}
    </TimeInputContainer>
  );
};

interface StyledTimelineBlockProps {
  type: 'timeline' | 'new' | 'modify';
  isSelectCaret: boolean;
  isOverHour: boolean;
}

const TimeInputContainer = styled.div<StyledTimelineBlockProps>`
  display: inline-flex;
  position: relative;
  justify-content: center;
  align-items: center;
  left: ${({ type }) => (type === 'modify' ? '-11px' : '0')};

  .time-picker {
    ${({ isOverHour, type }) => {
      const overHour = isOverHour;
      if (type === 'modify') {
        return css`
          width: ${overHour ? '100px' : '80px'};
          height: 20px;
        `;
      } else {
        return css`
          width: 100px;
          /* height: 24px; */
        `;
      }
    }}
    padding: 4px 4px;
    border: 1px solid #424242;
    box-sizing: border-box;
    border-radius: 3px;
    background-color: transparent;
    text-align: center;
    font-weight: bold;
    color: #f2f3f6;
    caret-color: ${({ isSelectCaret }) => (isSelectCaret ? 'transparent' : '#fff')};

    &:invalid {
      box-shadow: none;
    }
  }

  .btn-refresh {
    position: relative;
    left: 57px;
    height: 16px;
    overflow: hidden;
    cursor: pointer;
  }
`;

export default TimeInput;
